import React, { useEffect, useState, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    Button,
    Card,
    Flex,
    Heading,
    Image,
    Text,
    View,
    useTheme,
    Divider,
    Loader
} from '@aws-amplify/ui-react';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { useCustomSignOut } from '../hooks/useCustomSignOut';
import { isSameDomain } from '../utils/auth';
import { fetchWithRetry, FetchError } from '../utils/fetchWithRetry';
import { logError } from '../utils/errorLogger';
import { ErrorDisplay } from './ErrorDisplay';

interface UserInfo {
    username: string;
    userId: string;
    email?: string;
    // Add other attributes as needed
}

export const AuthenticatedHome: React.FC = () => {
    const { user, authStatus } = useAuthenticator((context) => [context.user, context.authStatus]);
    const customSignOut = useCustomSignOut();
    const { tokens } = useTheme();
    const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [redirectUri, setRedirectUri] = useState<string | null>(null);
    const [error, setError] = useState<FetchError | null>(null);
    const [isRetrying, setIsRetrying] = useState(false);

    // Amplifyが自動的にクッキーを管理するため、手動更新は不要

    // ユーザー情報取得処理（リトライ・タイムアウト機能付き）
    const getUserInfo = useCallback(async () => {
        if (authStatus !== 'authenticated' || !user) {
            console.log('⏳ Waiting for authentication...');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            setIsRetrying(false);

            console.log('🔄 Fetching user attributes with retry mechanism...');

            // タイムアウト・リトライ機能付きでユーザー属性を取得
            const attributes = await fetchWithRetry(
                () => fetchUserAttributes(),
                {
                    maxRetries: 3,
                    timeoutMs: 10000, // 10秒
                    initialDelayMs: 1000,
                    maxDelayMs: 4000
                }
            );

            console.log('✅ User attributes fetched successfully');

            // ユーザー情報を設定
            setUserInfo({
                username: user.username || '',
                userId: user.userId || '',
                email: attributes.email,
            });

        } catch (err: any) {
            console.error('❌ Failed to fetch user info after retries:', err);
            
            // エラーをログに記録
            const fetchError = err as FetchError;
            logError(fetchError, 'getUserInfo');
            
            // 認証エラーの場合は、エラーメッセージを表示せず静かにログアウト
            if (!fetchError.isRetryable && authStatus === 'authenticated') {
                console.log('🚪 Session invalid. Signing out silently...');
                setLoading(false);
                // エラーステートは設定しない（エラーメッセージを表示しない）
                customSignOut();
            } else {
                // リトライ可能なエラー（ネットワークエラーなど）の場合のみエラー表示
                setError(fetchError);
            }
        } finally {
            setLoading(false);
        }
    }, [authStatus, user, customSignOut]);

    // 初回マウント時にユーザー情報を取得
    useEffect(() => {
        // 認証済みの場合のみユーザー情報を取得
        if (authStatus === 'authenticated' && user) {
            getUserInfo();

            // redirect_uriパラメータをチェック
            const urlParams = new URLSearchParams(window.location.search);
            const redirectUriParam = urlParams.get('redirect_uri');
            if (redirectUriParam && isSameDomain(redirectUriParam)) {
                setRedirectUri(redirectUriParam);
            }
        }
    }, [getUserInfo, authStatus, user]);

    // リトライハンドラー
    const handleRetry = useCallback(() => {
        console.log('🔄 Manual retry triggered');
        setIsRetrying(true);
        getUserInfo();
    }, [getUserInfo]);

    // エラー表示
    if (error && !loading) {
        return (
            <ErrorDisplay
                error={error}
                onRetry={error.isRetryable ? handleRetry : undefined}
                onSignOut={!error.isRetryable ? customSignOut : undefined}
                showDetails={true}
            />
        );
    }

    // ローディング表示
    if (loading || !userInfo) {
        return (
            <View padding={tokens.space.large}>
                <Flex justifyContent="center" alignItems="center" height="100vh" direction="column" gap={tokens.space.medium}>
                    <Loader size="large" />
                    {isRetrying && (
                        <Text>再試行中...</Text>
                    )}
                </Flex>
            </View>
        );
    }

    const handleSignOut = async () => {
        try {
            // サインアウト前にエラーステートをクリア
            setError(null);
            setLoading(false);
            
            await customSignOut();
        } catch (error) {
            console.error('Error during sign out:', error);
        }
    };

    const handleRedirect = () => {
        if (redirectUri) {
            window.location.href = redirectUri;
        }
    };

    return (
        <View
            padding={tokens.space.large}
            maxWidth="480px"
            margin="0 auto"
        >
            <Card>
                <Flex direction="column" alignItems="center">
                    <Image
                        alt="YOLO JAPAN logo"
                        src="https://www.yolo-japan.com/assets/images/base/logo_header.png?v202407251030"
                        width="200px"
                        marginBottom={tokens.space.medium}
                    />
                    <Heading level={3}>
                        <FormattedMessage id="welcomeMessage" defaultMessage="Welcome to YOLO JAPAN" />
                    </Heading>
                </Flex>

                <Divider marginTop={tokens.space.large} marginBottom={tokens.space.large} />

                {userInfo && (
                    <Flex direction="column" gap={tokens.space.medium}>
                        <Heading level={5}>
                            <FormattedMessage id="userInfoHeading" defaultMessage="User Information" />
                        </Heading>
                        <Text>
                            <FormattedMessage id="userIdLabel" defaultMessage="User ID" />: {userInfo.userId}
                        </Text>
                        {userInfo.email && (
                            <Text>
                                <FormattedMessage id="emailLabel" defaultMessage="Email" />: {userInfo.email}
                            </Text>
                        )}
                    </Flex>
                )}

                <Flex justifyContent="center" marginTop={tokens.space.xl} gap={tokens.space.medium}>
                    {redirectUri && (
                        <Button onClick={handleRedirect} variation="primary">
                            <FormattedMessage id="returnToAppButton" defaultMessage="Return to Application" />
                        </Button>
                    )}
                    <Button onClick={handleSignOut}>
                        <FormattedMessage id="signOutButton" defaultMessage="Sign Out" />
                    </Button>
                </Flex>
            </Card>
        </View>
    );
};